'use client'

/**
 * Global error boundary for Fuel Estimator.
 *
 * Next.js renders this when an uncaught error escapes the route tree
 * (including the root layout). This is the last-resort error UI —
 * individual route error.tsx files handle most cases. We also auto-report
 * the error to Sentry so we learn about production issues immediately
 * instead of waiting for user complaints.
 *
 * purple brand theme matches the rest of the fuel app's styling.
 */

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'

export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string }
	reset: () => void
}) {
	useEffect(() => {
		Sentry.captureException(error)
	}, [error])

	return (
		<html lang="en">
			<body className="bg-black text-white flex items-center justify-center min-h-screen">
				<div className="text-center space-y-4 p-8">
					<h1 className="text-2xl font-bold">Something went wrong</h1>
					<p className="text-gray-400 text-sm">
						An unexpected error occurred while calculating your route. Our team has been notified.
					</p>
					<button
						type="button"
						onClick={reset}
						className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
					>
						Try again
					</button>
				</div>
			</body>
		</html>
	)
}
